'use client';
// Fenêtre « Alléger ce PDF / cette photo ? » (04/11/2026) — posée par le Shell
// quand un PDF ou une photo de plus de 1 Mo est choisi dans un champ de dépôt
// (`allegerAuDepot`, store.ts).
//
// Trois temps : la question (poids, pages ou dimensions, scan ou PDF natif), la
// compression (progression page à page), le résultat (avant → après, aperçu).
// Rien ne part au serveur ici : la fenêtre rend seulement le fichier retenu au
// champ qui l'a demandé, qui le contrôle et l'envoie comme avant.

import React, { useEffect, useRef, useState } from 'react';
import { useGed } from '@/lib/store';
import { Modale } from '@/components/ui';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
import { poidsMo } from '@/lib/depot';
import {
  natureAllegeable, examinerPdf, examinerImage, allegerPdf, allegerImage, GAIN_MINIMAL,
  type Allegement, type Nature, type Qualite,
} from '@/lib/alleger';

type Etat =
  | { t: 'question' }
  | { t: 'compression'; n: number; total: number }
  | { t: 'resultat'; a: Allegement }
  | { t: 'erreur'; message: string };

/** Ce qu'on dit du fichier avant de proposer. */
type Examen = { nature: 'pdf'; pages: number; texte: boolean } | { nature: 'image'; largeur: number; hauteur: number };

const texte: React.CSSProperties = { fontSize: 13, color: 'var(--text-strong)', margin: 0, lineHeight: 1.55 };
const note: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 };

export function AllegementFichier() {
  const { allegementEnAttente, repondreAllegement } = useGed();
  if (!allegementEnAttente) return null;
  // `key` : un nouveau fichier repart d'une fenêtre vierge.
  return <Fenetre key={`${allegementEnAttente.fichier.name}-${allegementEnAttente.fichier.lastModified}`} fichier={allegementEnAttente.fichier} repondre={repondreAllegement} />;
}

async function examiner(fichier: File): Promise<Examen | null> {
  const nature: Nature | null = await natureAllegeable(fichier);
  if (nature === 'pdf') return { nature, ...(await examinerPdf(fichier)) };
  if (nature === 'image') { const d = await examinerImage(fichier); return d && { nature, ...d }; }
  return null;
}

function Fenetre({ fichier, repondre }: { fichier: File; repondre: (f: File) => void }) {
  const [etat, setEtat] = useState<Etat>({ t: 'question' });
  const [qualite, setQualite] = useState<Qualite>('standard');
  const [examen, setExamen] = useState<Examen | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);
  const annule = useRef(false);

  useEffect(() => {
    let vivant = true;
    examiner(fichier).then((e) => { if (vivant) setExamen(e); }).catch(() => { /* la question se pose sans le détail */ });
    return () => { vivant = false; };
  }, [fichier]);

  useEffect(() => () => { if (apercu) URL.revokeObjectURL(apercu); }, [apercu]);

  const image = examen?.nature === 'image';
  const titre = image ? 'Alléger cette photo ?' : 'Alléger ce PDF ?';

  const garder = () => { annule.current = true; repondre(fichier); };

  const compresser = async () => {
    annule.current = false;
    setEtat({ t: 'compression', n: 0, total: examen?.nature === 'pdf' ? examen.pages : 0 });
    try {
      const a = image
        ? await allegerImage(fichier, qualite)
        : await allegerPdf(fichier, qualite, (n, total) => { if (!annule.current) setEtat({ t: 'compression', n, total }); });
      if (annule.current) return;
      setApercu(URL.createObjectURL(a.fichier));
      setEtat({ t: 'resultat', a });
    } catch (e) {
      if (!annule.current) setEtat({ t: 'erreur', message: e instanceof Error ? e.message : 'L’allègement a échoué.' });
    }
  };

  const nom = <strong style={{ wordBreak: 'break-all' }}>{fichier.name}</strong>;

  if (etat.t === 'compression') {
    const pct = etat.total ? Math.round((etat.n / etat.total) * 100) : 0;
    return (
      <Modale titre={titre} onClose={garder} largeur={460} sansSaisie
        pied={<PiedActions taille="sm" annuler={{ libelle: 'Garder l’original', onClick: garder }} />}>
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={texte}>Compression de {nom}…</p>
          <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Avancement de la compression"
            style={{ height: 8, borderRadius: 'var(--radius-pill, 999px)', background: 'var(--gray-100)', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--cyan-600)', transition: 'width 120ms linear' }} />
          </div>
          <p style={note}>{etat.total ? `Page ${etat.n} sur ${etat.total}` : 'Lecture du fichier…'} — tout se fait dans votre navigateur, rien n’est encore envoyé.</p>
        </div>
      </Modale>
    );
  }

  if (etat.t === 'resultat') {
    const { a } = etat;
    const gain = 1 - a.apres / a.avant;
    const utile = gain >= GAIN_MINIMAL;
    return (
      <Modale titre={titre} onClose={garder} largeur={460} sansSaisie
        pied={(
          <PiedActions taille="sm"
            annuler={{ libelle: 'Garder l’original', onClick: garder }}
            principale={utile ? { libelle: 'Utiliser la version allégée', onClick: () => repondre(a.fichier) } : undefined} />
        )}>
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={texte}>
            {nom} : <strong>{poidsMo(a.avant)}</strong> → <strong style={{ color: utile ? 'var(--green-700)' : 'var(--text-strong)' }}>{poidsMo(a.apres)}</strong>
            {utile && <> ({Math.round(gain * 100)} % de moins)</>}.
          </p>
          {utile ? (
            <p style={note}>
              Vérifiez que tout reste lisible avant de l’utiliser :{' '}
              {apercu && <a href={apercu} target="_blank" rel="noreferrer" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>ouvrir la version allégée</a>}.
              {qualite === 'standard' && ' Si de petits caractères sont flous, gardez l’original et reprenez en qualité « Nette ».'}
            </p>
          ) : (
            <p style={note}>Le gain est trop faible pour valoir la peine : le fichier original sera déposé tel quel.</p>
          )}
        </div>
      </Modale>
    );
  }

  const detail = !examen ? ''
    : examen.nature === 'pdf' ? ` pour ${examen.pages} page${examen.pages > 1 ? 's' : ''}`
      : ` pour ${examen.largeur} × ${examen.hauteur} pixels`;

  return (
    <Modale titre={titre} onClose={garder} largeur={460} sansSaisie
      pied={(
        <PiedActions taille="sm"
          annuler={{ libelle: 'Garder tel quel', onClick: garder }}
          principale={{ libelle: etat.t === 'erreur' ? 'Réessayer' : 'Compresser', onClick: () => void compresser() }} />
      )}>
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={texte}>
          {nom} pèse <strong>{poidsMo(fichier.size)}</strong>{detail}.{' '}
          {image
            ? 'Une photo se lit très bien à l’écran avec bien moins de pixels : la compresser évite de stocker du poids inutile.'
            : 'Un document de ce type se lit très bien à un poids bien moindre : le compresser évite de stocker du poids inutile.'}
        </p>
        {examen?.nature === 'pdf' && examen.texte && (
          <p style={{ ...note, color: 'var(--amber-800)', background: 'var(--amber-50)', padding: '8px 10px', borderRadius: 'var(--radius-sm)' }}>
            Ce PDF contient du texte. Une fois compressé, ses pages deviennent des images : le texte ne sera plus
            sélectionnable ni retrouvable par recherche. Pour un scan, rien n’est perdu.
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-body)', fontWeight: 600 }}>Qualité</span>
          <Segment ariaLabel="Qualité de la compression" valeur={qualite} onChange={setQualite}
            options={image ? [
              { valeur: 'standard', libelle: 'Standard', aide: '2 000 pixels de côté — lecture à l’écran' },
              { valeur: 'nette', libelle: 'Nette', aide: '2 800 pixels de côté — détails fins, étiquettes, écrans photographiés' },
            ] : [
              { valeur: 'standard', libelle: 'Standard', aide: '150 dpi — lecture à l’écran et impression courante' },
              { valeur: 'nette', libelle: 'Nette', aide: '200 dpi — petits caractères, tableaux de valeurs, signatures' },
            ]} />
        </div>
        {etat.t === 'erreur' && <p role="alert" style={{ ...note, color: 'var(--red-600)' }}>{etat.message}</p>}
        <p style={note}>La compression se fait dans votre navigateur ; vous verrez le résultat avant de choisir.</p>
      </div>
    </Modale>
  );
}
